import React from 'react';
import Text from './common/Text';
import '../styles/animations.css';

interface UserStatsProps {
  totalTimeSpent: string;
  totalSessions: number;
  averageAccuracy: number;
  currentStreak: number;
  isNewUser: boolean;
}

const UserStats: React.FC<UserStatsProps> = ({
  totalTimeSpent,
  totalSessions,
  averageAccuracy,
  currentStreak,
  isNewUser,
}) => {
  if (isNewUser) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Welcome to Your Learning Journey! 🚀
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Start your first practice session to track your progress and see your learning statistics.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 opacity-50">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Current Streak</div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-white">0</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Days</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Total Time Spent</div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-white">0h 0m</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Learning & Practicing</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Total Sessions</div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-white">0</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Practice Sessions Completed</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Average Accuracy</div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-white">0%</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Across All Sessions</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-1">
          <div className="text-gray-700 dark:text-gray-300 text-sm font-medium">Current Streak</div>
          <div className="relative group">
            <span className="text-gray-400 dark:text-gray-500 cursor-help text-sm">ⓘ</span>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg text-sm text-gray-600 dark:text-gray-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              Complete practice sessions on consecutive days to maintain your streak.
            </div>
          </div>
        </div>
        <div className="text-2xl font-semibold text-amber-600 dark:text-amber-400">{currentStreak}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Days</div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">Total Time Spent</div>
        <div className="text-2xl font-semibold text-gray-900 dark:text-white">{totalTimeSpent}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Learning & Practicing</div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">Total Sessions</div>
        <div className="text-2xl font-semibold text-gray-900 dark:text-white">{totalSessions}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Practice Sessions Completed</div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">Average Accuracy</div>
        <div className="text-2xl font-semibold text-gray-900 dark:text-white">{averageAccuracy}%</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Across All Sessions</div>
      </div>
    </div>
  );
};

export default UserStats;